import React, { useState } from 'react';
import { Globe, Shield, FileText, CheckCircle, Users } from 'lucide-react';
import { getTranslation, getArrayTranslation } from '../../utils/i18n/translations';

const GDPRCompliance = ({ onAccept, onDecline, organizationName = "Your Organization" }) => {
  const [language, setLanguage] = useState('en');
  const [accepted, setAccepted] = useState(false);
  const [signature, setSignature] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({});

  const t = (key, variables = {}) => getTranslation(language, key, { organization: organizationName, ...variables });
  const tArray = (key) => getArrayTranslation(language, key);

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!accepted) {
      newErrors.terms = t('mustAcceptTerms');
    }

    if (!signature.trim()) {
      newErrors.signature = 'Full name is required for digital signature';
    }

    if (!phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      // Log consent for GDPR compliance
      const consentData = {
        timestamp: new Date().toISOString(),
        language,
        signature: signature.trim(),
        phone: phone.trim(),
        email: email.trim(),
        userAgent: navigator.userAgent,
        ipAddress: 'logged-server-side', // Server will log actual IP
        accepted: true
      };

      onAccept(consentData);
    }
  };

  const handleDecline = () => {
    const declineData = {
      timestamp: new Date().toISOString(),
      accepted: false,
      language
    };
    onDecline(declineData);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 flex items-center justify-center p-2 sm:p-4">
      <div className="max-w-4xl w-full bg-white/10 backdrop-blur-lg rounded-2xl sm:rounded-3xl shadow-2xl border border-white/20 overflow-hidden mx-2 sm:mx-0">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 p-4 sm:p-6 border-b border-white/10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="p-2 sm:p-3 bg-blue-500/20 rounded-xl sm:rounded-2xl border border-blue-400/30">
                <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-blue-300" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-white">{t('wifiPortal')}</h1>
                <p className="text-sm sm:text-base text-blue-200">{t('gdprCompliance')}</p>
              </div>
            </div>
            
            {/* Language Selector */}
            <div className="flex items-center space-x-2 w-full sm:w-auto">
              <Globe className="h-4 w-4 sm:h-5 sm:w-5 text-white/60" />
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-lg px-2 py-1 sm:px-3 sm:py-2 text-white text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-blue-400/50 flex-1 sm:flex-none"
                style={{ color: 'white' }}
              >
                <option value="en" style={{ color: 'black' }}>English</option>
                <option value="fr" style={{ color: 'black' }}>Français</option>
                <option value="es" style={{ color: 'black' }}>Español</option>
                <option value="de" style={{ color: 'black' }}>Deutsch</option>
                <option value="it" style={{ color: 'black' }}>Italiano</option>
                <option value="pt" style={{ color: 'black' }}>Português</option>
                <option value="nl" style={{ color: 'black' }}>Nederlands</option>
                <option value="sv" style={{ color: 'black' }}>Svenska</option>
                <option value="da" style={{ color: 'black' }}>Dansk</option>
                <option value="no" style={{ color: 'black' }}>Norsk</option>
                <option value="fi" style={{ color: 'black' }}>Suomi</option>
                <option value="pl" style={{ color: 'black' }}>Polski</option>
                <option value="cs" style={{ color: 'black' }}>Čeština</option>
                <option value="hu" style={{ color: 'black' }}>Magyar</option>
                <option value="ro" style={{ color: 'black' }}>Română</option>
                <option value="bg" style={{ color: 'black' }}>Български</option>
                <option value="ar" style={{ color: 'black' }}>العربية</option>
                <option value="zh" style={{ color: 'black' }}>中文</option>
                <option value="ja" style={{ color: 'black' }}>日本語</option>
                <option value="ko" style={{ color: 'black' }}>한국어</option>
              </select>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 max-h-[70vh] overflow-y-auto">
          
          {/* Welcome */}
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">
              {t('welcome')}
            </h2>
            <p className="text-sm sm:text-base text-white/80 leading-relaxed max-w-3xl mx-auto px-2">
              {t('complianceRequired')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-8">
            
            {/* Terms of Use */}
            <div className="bg-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-white/10">
              <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
                <FileText className="h-5 w-5 sm:h-6 sm:w-6 text-blue-300" />
                <h3 className="text-lg sm:text-xl font-semibold text-white">{t('termsOfUse')}</h3>
              </div>
              <p className="text-sm sm:text-base text-white/80 mb-3 sm:mb-4">{t('termsContent')}</p>
              <ul className="space-y-2">
                {tArray('termsPoints').map((point, index) => (
                  <li key={index} className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-white/70 text-xs sm:text-sm">{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Privacy Notice */}
            <div className="bg-white/5 rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-white/10">
              <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
                <Shield className="h-5 w-5 sm:h-6 sm:w-6 text-purple-300" />
                <h3 className="text-lg sm:text-xl font-semibold text-white">{t('privacyNotice')}</h3>
              </div>
              
              <div className="mb-3 sm:mb-4">
                <p className="text-sm sm:text-base text-white/80 mb-2">{t('dataCollection')}</p>
                <ul className="space-y-1">
                  {tArray('dataPoints').map((point, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-purple-400 rounded-full"></div>
                      <span className="text-white/70 text-xs sm:text-sm">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mb-3 sm:mb-4">
                <p className="text-sm sm:text-base text-white/80 mb-2">{t('purposeTitle')}</p>
                <ul className="space-y-1">
                  {tArray('purposePoints').map((point, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-400 rounded-full"></div>
                      <span className="text-white/70 text-xs sm:text-sm">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-blue-500/10 border border-blue-400/20 rounded-lg p-2 sm:p-3 mb-3 sm:mb-4">
                <p className="text-blue-200 text-xs sm:text-sm">
                  {t('dataRetention', { months: '6' })}
                </p>
              </div>

              <div>
                <p className="text-sm sm:text-base text-white/80 mb-2">{t('userRights')}</p>
                <ul className="space-y-1">
                  {tArray('rightsPoints').map((point, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-yellow-400 rounded-full"></div>
                      <span className="text-white/70 text-xs sm:text-sm">
                        {point.replace('{email}', 'privacy@hotspot.com')}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Consent Form */}
          <form onSubmit={handleSubmit} className="bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-400/20 rounded-xl sm:rounded-2xl p-4 sm:p-6">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
              <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-300" />
              <h3 className="text-lg sm:text-xl font-semibold text-white">{t('consentTitle')}</h3>
            </div>

            <p className="text-sm sm:text-base text-white/80 mb-4 sm:mb-6">{t('consentText')}</p>

            {/* Consent Checkbox */}
            <div className="mb-4 sm:mb-6">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="mt-1 w-4 h-4 sm:w-5 sm:h-5 rounded border-2 border-white/30 bg-white/10 text-green-500 focus:ring-2 focus:ring-green-400/50"
                />
                <span className="text-sm sm:text-base text-white/90 leading-relaxed">
                  {t('consentCheckbox')}
                </span>
              </label>
              {errors.terms && (
                <p className="text-red-400 text-xs sm:text-sm mt-2 ml-7 sm:ml-8">{errors.terms}</p>
              )}
            </div>

            {/* Name Signature */}
            <div className="mb-4 sm:mb-6">
              <label className="block text-sm sm:text-base text-white/90 font-medium mb-2">
                Full Name (Digital Signature) *
              </label>
              <input
                type="text"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder="Enter your full name"
                className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-white/10 border border-white/20 rounded-lg sm:rounded-xl text-sm sm:text-base text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent"
              />
              <p className="text-white/60 text-xs sm:text-sm mt-1">This serves as your digital signature</p>
              {errors.signature && (
                <p className="text-red-400 text-xs sm:text-sm mt-1">{errors.signature}</p>
              )}
            </div>

            {/* Phone Number */}
            <div className="mb-4 sm:mb-6">
              <label className="block text-sm sm:text-base text-white/90 font-medium mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter your phone number"
                className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-white/10 border border-white/20 rounded-lg sm:rounded-xl text-sm sm:text-base text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent"
              />
              {errors.phone && (
                <p className="text-red-400 text-xs sm:text-sm mt-1">{errors.phone}</p>
              )}
            </div>

            {/* Email Address */}
            <div className="mb-4 sm:mb-6">
              <label className="block text-sm sm:text-base text-white/90 font-medium mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="w-full px-3 py-2 sm:px-4 sm:py-3 bg-white/10 border border-white/20 rounded-lg sm:rounded-xl text-sm sm:text-base text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent"
              />
              {errors.email && (
                <p className="text-red-400 text-xs sm:text-sm mt-1">{errors.email}</p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 sm:gap-4">
              <button
                type="submit"
                disabled={!accepted || !signature.trim() || !phone.trim() || !email.trim()}
                className="w-full bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 disabled:from-gray-500 disabled:to-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-lg sm:rounded-xl transition-all duration-300 transform hover:scale-105 disabled:hover:scale-100 flex items-center justify-center space-x-2 shadow-lg text-sm sm:text-base"
              >
                <Users className="h-4 w-4 sm:h-5 sm:w-5" />
                <span>{t('connectToWifi')}</span>
              </button>
              
              <button
                type="button"
                onClick={handleDecline}
                className="w-full sm:w-auto sm:self-center bg-white/10 hover:bg-white/20 border border-white/30 text-white font-semibold py-2 sm:py-3 px-4 sm:px-6 rounded-lg sm:rounded-xl transition-all duration-300 text-sm sm:text-base"
              >
                {t('decline')}
              </button>
            </div>
          </form>

          {/* Footer */}
          <div className="text-center mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-white/10">
            <p className="text-white/60 text-xs sm:text-sm">
              {t('poweredBy')}
            </p>
            <p className="text-white/40 text-xs mt-1 sm:mt-2">
              {t('lastUpdated', { date: new Date().toLocaleDateString(
                language === 'fr' ? 'fr-FR' : 
                language === 'es' ? 'es-ES' :
                language === 'de' ? 'de-DE' :
                language === 'it' ? 'it-IT' :
                language === 'pt' ? 'pt-PT' :
                language === 'nl' ? 'nl-NL' :
                language === 'sv' ? 'sv-SE' :
                language === 'da' ? 'da-DK' :
                language === 'no' ? 'nb-NO' :
                language === 'fi' ? 'fi-FI' :
                language === 'pl' ? 'pl-PL' :
                language === 'cs' ? 'cs-CZ' :
                language === 'hu' ? 'hu-HU' :
                language === 'ro' ? 'ro-RO' :
                language === 'bg' ? 'bg-BG' :
                language === 'ar' ? 'ar-SA' :
                language === 'zh' ? 'zh-CN' :
                language === 'ja' ? 'ja-JP' :
                language === 'ko' ? 'ko-KR' : 'en-US') })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GDPRCompliance;
